/**
 * Data of the レジ開閉 screens (T07-26): the open shift with its live totals (updates itself from
 * the register's stream — a sale, a return, a 取り置き deposit changes the drawer), the history,
 * and printing a report through the same print queue as the receipts (T07-25).
 */
import type { PosClient } from "@tcg/api-client";
import { useQuery } from "@tanstack/react-query";
import { useRealtime } from "@tcg/realtime/react";

import { usePos } from "@/app/context";
import { call } from "@/lib/api";
import type { PrinterManager } from "@/lib/printer";
import { POS_TOPICS } from "@/lib/sse";

export const currentShiftKey = (shiftId: string | null) =>
  ["pos-shifts", "detail", shiftId ?? "none", "current"] as const;
export const shiftHistoryKey = ["pos-shifts", "list", "history"] as const;

export function useCurrentShift(shiftId: string | null) {
  const { api } = usePos();
  const query = useQuery({
    queryKey: currentShiftKey(shiftId),
    queryFn: () => call(() => api.GET("/v1/pos/shifts/current")),
    enabled: Boolean(shiftId),
    staleTime: 5_000,
  });
  // a sale / return / hold deposit moved money: the drawer total follows (R21, ≤ 2 s)
  useRealtime([POS_TOPICS.sale, POS_TOPICS.hold], () => void query.refetch(), {
    enabled: Boolean(shiftId),
    onResync: () => void query.refetch(),
  });
  return query;
}

export function useShiftHistory(enabled: boolean) {
  const { api } = usePos();
  return useQuery({
    queryKey: shiftHistoryKey,
    queryFn: () => call(() => api.GET("/v1/pos/shifts", { params: { query: { limit: 24 } } })),
    enabled,
  });
}

export type ReportKind = "x" | "z";

/** The report's ESC/POS bytes from pos-service → the print queue (never throws). */
export async function printShiftReport(options: {
  api: PosClient;
  printer: PrinterManager;
  shiftId: string;
  kind: ReportKind;
}): Promise<boolean> {
  const { api, printer, shiftId, kind } = options;
  const paperMm = printer.config.paperWidth;
  try {
    const answer =
      kind === "x"
        ? await call(() =>
            api.GET("/v1/pos/shifts/{shift_id}/x-report", {
              params: { path: { shift_id: shiftId }, query: { format: "escpos", paperMm } },
            }),
          )
        : await call(() =>
            api.GET("/v1/pos/shifts/{shift_id}/z-report", {
              params: { path: { shift_id: shiftId }, query: { format: "escpos", paperMm } },
            }),
          );
    if (!("data" in answer) || typeof answer.data !== "string") {
      return false;
    }
    const job = await printer.queue.submit({
      label: kind === "x" ? "X" : "Z",
      data: answer.data,
    });
    return job.status === "done";
  } catch {
    return false;
  }
}
